import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CurrentOrPreviousGroup, School } from '../../../../../../api/groups';
import { SchoolYearValue, useResultsDataContext } from '../../ResultsDataContext';
import { useFilterInLocalStorage } from '../../../components/per-student/hooks/useFilterInLocalStorage';
import { FilterSearchParam } from '../../../../../../shared-components/elementis/types/FilterSearchParam';
import TABS from '../../../../../../shared-components/elementis/types/ResultsPageTabs';

interface Opts {
  selectedSchoolYear: SchoolYearValue | null;
  selectedSchool: School | null;
  selectedTab: 'STUDENT' | 'EXERCISE';
  setDefaultFilters: () => void;
}

export function useSelectedGroups({ selectedTab, setDefaultFilters, selectedSchoolYear, selectedSchool }: Opts) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { fetchMembersForGroups, modules, groups } = useResultsDataContext();
  const { getStoredData } = useFilterInLocalStorage(modules[0]?.id);

  const selectedGroupIds = searchParams.get(FilterSearchParam.Groups);

  const selectedGroups = useMemo(() => {
    const selectedGroupIdsArray = selectedGroupIds?.split(',') || [];

    if (selectedTab === 'STUDENT') {
      const selectedGroup = groups.find(g => g.id === selectedGroupIdsArray[0]);
      if (selectedGroup) return [selectedGroup];
    } else {
      if (!selectedGroupIdsArray.length) {
        return groups.filter(
          g =>
            g.acceptedMemberCount > 0 && g.schoolYear === selectedSchoolYear?.id && g.school.id === selectedSchool?.id,
        );
      }

      const selectedGroups = selectedGroupIdsArray
        .map(id => groups.find(g => g.id === id))
        .filter(Boolean) as CurrentOrPreviousGroup[];

      if (selectedGroups.length === selectedGroupIdsArray.length) return selectedGroups;
    }

    setDefaultFilters();
    return [];
  }, [groups, selectedGroupIds, selectedSchool?.id, selectedSchoolYear?.id, selectedTab, setDefaultFilters]);

  const previousSelectedGroupsRef = useRef<CurrentOrPreviousGroup[]>([]);

  useEffect(() => {
    const isSelectedGroupsChanged =
      selectedGroups.length !== previousSelectedGroupsRef.current.length ||
      selectedGroups.some(g => !previousSelectedGroupsRef.current.some(pg => pg.id === g.id));

    if (selectedGroups.length && isSelectedGroupsChanged) {
      fetchMembersForGroups(selectedGroups);
      previousSelectedGroupsRef.current = selectedGroups;
    }
  }, [fetchMembersForGroups, selectedGroups]);

  const syncSelectedGroupsWithSearchParams = useCallback(
    (groups: CurrentOrPreviousGroup[]) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set(FilterSearchParam.Groups, groups.map(g => g.id).join(','));
        newParams.set(FilterSearchParam.School, groups[0].school.id);
        newParams.set(FilterSearchParam.SchoolYear, groups[0].schoolYear);
        return newParams;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (selectedGroupIds || selectedTab === TABS.EXERCISE) return;

    const storedData = getStoredData();
    if (storedData === null) return;

    const storedGroups = groups.filter(g => storedData.groupIds.includes(g.id));
    if (storedGroups.length === 0) return;

    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set(FilterSearchParam.Groups, storedGroups.map(g => g.id).join(','));
      return newParams;
    });
  }, [getStoredData, groups, selectedGroupIds, selectedTab, setSearchParams, syncSelectedGroupsWithSearchParams]);

  return [selectedGroups, syncSelectedGroupsWithSearchParams] as const;
}
